import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { OctagonX, RotateCcw } from 'lucide-react';
import { BULK_OPERATIONS } from '@cp/shared';
import { Banner, Button, Drawer, ProgressBar, Spinner, StatusPill } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { useBulkRun, useRetryBulkRun, useStopBulkRun } from '@/hooks/useBulk';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import type { StatusPresentation } from '@/utils/labels';

export const BULK_ITEM: Record<string, StatusPresentation> = {
  pending: { tone: 'pending', label: 'В очереди', glyph: '○', hint: 'Ожидает запуска' },
  queued: { tone: 'maintenance', label: 'Выполняется', glyph: '◌', hint: 'Задача сайта в работе' },
  done: { tone: 'healthy', label: 'Готово', glyph: '●', hint: 'Выполнено' },
  failed: { tone: 'down', label: 'Не удалось', glyph: '■', hint: 'Задача сайта не выполнилась' },
  skipped: { tone: 'unknown', label: 'Пропущен', glyph: '—', hint: 'Действие неприменимо к сайту' },
  cancelled: { tone: 'unknown', label: 'Отменено', glyph: '—', hint: 'Выполнение остановлено' },
};

export const BULK_RUN: Record<string, StatusPresentation> = {
  running: { tone: 'maintenance', label: 'Идёт', glyph: '◌', hint: 'Сайты обрабатываются' },
  completed: { tone: 'healthy', label: 'Завершено', glyph: '●', hint: 'Все сайты обработаны' },
  stopped: { tone: 'degraded', label: 'Остановлено', glyph: '▲', hint: 'Часть сайтов не обработана' },
};

/**
 * One bulk run (ADR-0046) as it goes: every site, where it stands and why,
 * and the two things to do with a run — stop what has not started, and try
 * again what failed.
 */
export function BulkRunDrawer({ id, onClose }: { readonly id: string | null; readonly onClose: () => void }): ReactNode {
  const run = useBulkRun(id);
  const stop = useStopBulkRun(id ?? '', {
    onSuccess: () => toast.success('Выполнение остановлено', 'Запущенные задачи будут завершены'),
    onError: (error) => toast.error('Не удалось остановить', errorText(error)),
  });
  const retry = useRetryBulkRun(id ?? '', {
    onSuccess: () => toast.success('Повтор запущен'),
    onError: (error) => toast.error('Не удалось повторить', errorText(error)),
  });
  const view = run.data;
  const meta = view ? BULK_OPERATIONS[view.operation] : null;
  const again = view ? view.counts.failed + view.counts.cancelled : 0;

  return (
    <Drawer
      open={id !== null}
      title={meta ? `Массовое действие: ${meta.label.toLowerCase()}` : 'Массовое действие'}
      {...(view ? { description: `${view.createdBy} · ${formatRelative(view.createdAt)}` } : {})}
      onClose={onClose}
      width="md"
      footer={
        <>
          {view?.status === 'running' && !meta?.instant ? (
            <Button icon={OctagonX} busy={stop.isPending} onClick={() => stop.mutate()}>
              Остановить остальные
            </Button>
          ) : null}
          {view && view.status !== 'running' && !meta?.instant && again > 0 ? (
            <Button icon={RotateCcw} busy={retry.isPending} onClick={() => retry.mutate()}>
              Повторить неудавшиеся ({again})
            </Button>
          ) : null}
          <Button onClick={onClose}>Закрыть</Button>
        </>
      }
    >
      {run.isPending ? (
        <Spinner />
      ) : run.isError ? (
        <ErrorState error={run.error} />
      ) : view && meta ? (
        <div className="stack">
          <div className="row-tight row-wrap">
            <StatusPill map={BULK_RUN} status={view.status} />
            <span className="muted">
              готово {view.counts.done} · выполняется {view.counts.queued} · в очереди {view.counts.pending} · не удалось {view.counts.failed} · пропущено{' '}
              {view.counts.skipped}
              {view.counts.cancelled > 0 ? ` · отменено ${view.counts.cancelled}` : ''}
            </span>
          </div>
          <ProgressBar
            value={view.counts.done + view.counts.failed + view.counts.skipped + view.counts.cancelled}
            max={Math.max(1, view.items.length)}
            label="Выполнено сайтов"
            tone={view.counts.failed > 0 ? 'degraded' : view.status === 'running' ? 'accent' : 'healthy'}
          />
          {meta.risky ? (
            <Banner tone="info">
              Сначала выполняется на одном сайте, затем на остальных. После двух сбоев выполнение останавливается.
            </Banner>
          ) : null}
          <ul className="list-plain bulk-items">
            {view.items.map((item) => (
              <li key={item.siteId} className="row-between">
                <span className="stack stack-tight">
                  <Link to={`/sites/${item.siteId}`}>{item.siteName}</Link>
                  {item.reason ? <span className="muted text-sm">{item.reason}</span> : null}
                </span>
                <StatusPill map={BULK_ITEM} status={item.state} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Drawer>
  );
}
